import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useResearchContext } from '../../ResearchContext.provider';
import { Button } from 'components/form/Button';
import { ShareIcon, ExpandIcon, QuestionIcon, LinkIcon, CloseIcon, TeamWorkIcon, InvestorIcon, CpuIcon, LinkedInIcon, ValidationIcon, CancelIcon, LaunchIcon, RegulationsRoundIcon, DocumentIcon, HandshakeIcon, DashboardIcon, XIcon } from 'components/Icons';
import './UserMessageActions.scss';
import DropDown, { DropdownMenuItem } from 'components/Dropdown/Dropdown.component';

export type UserMessageActionsProps = {
  shouldShowButton: boolean;
  isLastQuestion: boolean;
  index: number;
  hasImages: boolean;
  hasFollowUpQuestions: boolean;
  showFollowUpQuestions: boolean;
  sourcesLength: number;
  showSources: boolean;
  shouldShowExpandButton: boolean;
  onToggleFollowUpQuestions: () => void;
  onToggleSources: () => void;
  onReportFollowUpClick?: (question: string) => void;
};

const EXPAND_ICONS = {
    'partnerships':  { color: 'var(--danger-color-500)', icon: <TeamWorkIcon /> },
    'investments':  { color: 'var(--accent-color-2)', icon: <InvestorIcon /> },
    'technologies':  { color: 'var(--primary-color-dark-900)', icon: <CpuIcon /> },
    'insights': { color: 'var(--alert-color-1)', icon:  <ExpandIcon /> },
    'linkedin post': { color: 'var(--primary-color-500)', icon: <LinkedInIcon /> },
    // SWOT Analysis - unique icons and colors
    'strengths': { color: 'var(--success-color-500)', icon: <ValidationIcon /> },
    'weaknesses': { color: 'var(--danger-color-500)', icon: <CancelIcon /> },
    'opportunities': { color: 'var(--accent-color-2)', icon: <LaunchIcon /> },
    'threats': { color: 'var(--alert-color-1)', icon: <XIcon /> },
    // PESTL Analysis - unique icons and colors
    'political': { color: 'var(--primary-color-500)', icon: <RegulationsRoundIcon /> },
    'economic': { color: 'var(--primary-color-400)', icon: <DashboardIcon /> },
    'social': { color: 'var(--accent-color-1)', icon: <HandshakeIcon /> },
    'technological': { color: 'var(--primary-color-dark-900)', icon: <CpuIcon /> },
    'legal': { color: 'var(--primary-color-dark-800)', icon: <DocumentIcon /> },
    'regulatory': { color: 'var(--primary-color-dark-800)', icon: <DocumentIcon /> },
  };

const findIconByName = (name: string) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('linkedin post')) {
    return EXPAND_ICONS['linkedin post'];
  }
  
  // SWOT Analysis icons
  if (lowerName.includes('strengths')) {
    return EXPAND_ICONS['strengths'];
  }
  if (lowerName.includes('weaknesses')) {
    return EXPAND_ICONS['weaknesses'];
  }
  if (lowerName.includes('opportunities')) {
    return EXPAND_ICONS['opportunities'];
  }
  if (lowerName.includes('threats')) {
    return EXPAND_ICONS['threats'];
  }
  
  // PESTL Analysis icons
  if (lowerName.includes('political')) {
    return EXPAND_ICONS['political'];
  }
  if (lowerName.includes('economic')) {
    return EXPAND_ICONS['economic'];
  }
  if (lowerName.includes('social') && !lowerName.includes('technological')) {
    return EXPAND_ICONS['social'];
  }
  if (lowerName.includes('technological')) {
    return EXPAND_ICONS['technological'];
  }
  if (lowerName.includes('legal') || lowerName.includes('regulatory')) {
    return EXPAND_ICONS['legal'];
  }
  
  // Fallback to original logic
  const iconName = name.split(' ').pop()?.toLowerCase();
  return EXPAND_ICONS[iconName as keyof typeof EXPAND_ICONS || ''] || null;
};

const ColorfulPresentationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="presentation_gradient" x1="2" y1="4" x2="22" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#4158D0" />
        <stop offset="50%" stopColor="#C850C0" />
        <stop offset="100%" stopColor="#FFCC70" />
      </linearGradient>
    </defs>
    <path d="M2.5 7C2.5 5.61929 3.61929 4.5 5 4.5H19C20.3807 4.5 21.5 5.61929 21.5 7V15C21.5 16.3807 20.3807 17.5 19 17.5H5C3.61929 17.5 2.5 16.3807 2.5 15V7Z" fill="url(#presentation_gradient)" />
    <path d="M7 11L10 8L13 11L17 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 17.5V21.5" stroke="url(#presentation_gradient)" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M8 21.5H16" stroke="url(#presentation_gradient)" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const UserMessageActions: React.FC<UserMessageActionsProps> = ({
  shouldShowButton,
  isLastQuestion,
  index,
  hasImages,
  hasFollowUpQuestions,
  showFollowUpQuestions,
  sourcesLength,
  showSources,
  shouldShowExpandButton,
  onToggleFollowUpQuestions,
  onToggleSources,
  onReportFollowUpClick,
}) => {
  const { state, actions } = useResearchContext();
  const { longReportType, streaming, typing, imagesLoading, routerReportType, isFuqLoading, reportFollowUpQuestions: reportFollowUpQuestionsMap, generatedContentMap, chat, isShareMode, sharedArtifactType } = state;
  const { 
    onShareClick, 
    onExploreChartClick,
    shareArtifact,
    onReportFollowUpClick: contextReportFollowUpClick,
    scrollToBottom,
  } = actions;

  // State for showing inline share copied message
  const [showShareCopied, setShowShareCopied] = useState(false);
  const shareDropdownRef = useRef<HTMLDivElement>(null);

  // Auto-hide share copied message after 3 seconds
  useEffect(() => {
    if (showShareCopied) {
      const timer = setTimeout(() => setShowShareCopied(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showShareCopied]);

  // Presentation dropdown items
  const presentationDropdownItems: DropdownMenuItem[] = useMemo(() => [
    {
      name: 'Executive Summary',
      value: 'Executive Summary',
      icon: <i className="material-icons">summarize</i>,
      color: 'var(--primary-color-500)'
    },
    {
      name: 'Presentation',
      value: 'Presentation',
      icon: <i className="material-icons">slideshow</i>,
      color: 'var(--primary-color-500)'
    },
    {
      name: 'Create LinkedIn Post',
      value: 'Create LinkedIn Post',
      icon: <LinkedInIcon size={20} fill="var(--linkedin-brand-color)" />,
      color: 'var(--linkedin-brand-color)'
    }
  ], []);

  // Check if Presentation dropdown should be shown
  const shouldShowPresentationDropdown = useMemo(() => {
    return chat.length > 0 && !typing && !streaming && isLastQuestion;
  }, [chat.length, typing, streaming, isLastQuestion]);

  // Build share dropdown items based on generatedContentMap for this specific question
  const shareDropdownItems: DropdownMenuItem[] = useMemo(() => {
    // In share mode, show the appropriate share option based on sharedArtifactType
    if (isShareMode) {
      // Determine the correct share option name based on sharedArtifactType
      let shareName = 'Share Report';
      let shareIcon = <ShareIcon />;
      let shareColor = 'var(--primary-color-500)';
      
      if (sharedArtifactType === 'executive-summary') {
        shareName = 'Share Executive Summary';
        shareIcon = <i className="material-icons" style={{ fontSize: '20px' }}>summarize</i>;
        shareColor = 'var(--success-color-500)';
      } else if (sharedArtifactType === 'presentation') {
        shareName = 'Share Presentation';
        shareIcon = <i className="material-icons" style={{ fontSize: '20px' }}>slideshow</i>;
        shareColor = 'var(--accent-color-1)';
      } else if (sharedArtifactType === 'linkedin-post') {
        shareName = 'Share LinkedIn Post';
        shareIcon = <LinkedInIcon size={20} />;
        shareColor = 'var(--linkedin-brand-color)';
      }
      
      return [{
        name: shareName,
        value: 'share-current',
        icon: shareIcon,
        color: shareColor,
        disabled: false,
        onClick: () => {
          onShareClick?.(index + 1); // This will copy current URL in share mode
          setShowShareCopied(true);
        }
      }];
    }
    
    // Normal mode - original logic
    // Check if there is an answer for this question
    // The question is at `index`. The answer should be at `index + 1`.
    // Valid answer exists if index+1 is within bounds and role is assistant
    const hasAnswer = chat && chat.length > index + 1 && chat[index + 1]?.role === 'assistant';
    const isAnswerStreaming = streaming && chat.length === index + 2; // Rough check if currently streaming this answer
    
    // Share Report is enabled only if answer is fully generated (has answer and not streaming? or just has answer?)
    // User said: "bir soruyla alakali cevap tamamen olusturulduysa ... aktif hale gelsin"
    // "tamamen olusturulduysa" implies not streaming.
    const isShareReportEnabled = hasAnswer && (!isAnswerStreaming || !streaming);

    // Get the question content for artifact sharing
    const questionContent = chat?.[index]?.content || '';

    const items: DropdownMenuItem[] = [
      {
        name: 'Share Report',
        value: 'share-report',
        icon: <ShareIcon />,
        color: 'var(--primary-color-500)',
        disabled: !isShareReportEnabled,
        onClick: () => {
          if (isShareReportEnabled) {
            onShareClick?.(index + 1);
            setShowShareCopied(true);
          }
        }
      }
    ];

    // Add specific share options based on what content was generated for THIS question
    const generatedArtifacts = generatedContentMap?.[index] || [];
    
    generatedArtifacts.forEach((artifact, artifactIdx) => {
        let icon = <i className="material-icons">article</i>;
        let color = 'var(--primary-color-500)';
        let name = 'Share Content';
        
        if (artifact.type === 'presentation') {
            icon = <i className="material-icons" style={{ fontSize: '20px' }}>slideshow</i>;
            color = 'var(--accent-color-1)';
            name = 'Share Presentation';
        } else if (artifact.type === 'linkedin-post') {
            icon = <LinkedInIcon size={20} />;
            color = 'var(--linkedin-brand-color)';
            name = 'Share LinkedIn Post';
        } else if (artifact.type === 'executive-summary') {
            icon = <i className="material-icons" style={{ fontSize: '20px' }}>summarize</i>;
            color = 'var(--success-color-500)';
            name = 'Share Executive Summary';
        }
        
        // Get artifact content from chat
        const artifactContent = chat?.[artifact.index]?.content || '';
        
        // Sources are stored in the USER message (index), not the assistant message (index+1)
        const originalAnswerSources = chat?.[index]?.sources || [];
        
        // Only disable the LAST artifact if streaming (that's the one currently being generated)
        // Previous artifacts are already complete
        const isLastArtifact = artifactIdx === generatedArtifacts.length - 1;
        const isArtifactReady = !(streaming && isLastArtifact);
        
        items.push({
            name,
            value: `share-${artifact.type}`,
            icon,
            color,
            disabled: !isArtifactReady,
            onClick: () => {
                // Get sources at click time (not at useMemo time)
                const sourcesAtClickTime = chat?.[index]?.sources || [];
                if (isArtifactReady) {
                    shareArtifact?.(artifactContent, questionContent, sourcesAtClickTime, artifact.type);
                    setShowShareCopied(true);
                }
            }
        });
    });

    return items;
  }, [generatedContentMap, onShareClick, shareArtifact, index, chat, streaming, isShareMode, sharedArtifactType]);

  const resolvedExpandReportType = useMemo(() => {
    // For SWOT and PESTL reports, prioritize longReportType over routerReportType
    // This ensures that SWOT/PESTL highlights are shown from the first question
    if (longReportType && (longReportType === 'swotReport' || longReportType === 'pestlReport')) {
      if (reportFollowUpQuestionsMap?.[longReportType]?.length) {
        return longReportType;
      }
    }
    // For other report types, check routerReportType first
    if (routerReportType && reportFollowUpQuestionsMap?.[routerReportType]?.length) {
      return routerReportType;
    }
    // Fallback to longReportType
    if (longReportType && reportFollowUpQuestionsMap?.[longReportType]?.length) {
      return longReportType;
    }
    return null;
  }, [routerReportType, longReportType, reportFollowUpQuestionsMap]);

  const expandButtonItems: DropdownMenuItem[] = useMemo(() => {
    if (!resolvedExpandReportType) {
      return [];
    }
    const questions = reportFollowUpQuestionsMap?.[resolvedExpandReportType];
    if (!questions || !Array.isArray(questions)) {
      return [];
    }
    return questions
      .filter((question: string) => {
        if (resolvedExpandReportType === 'swotReport') {
          return !question.toLowerCase().includes('strategic takeaways');
        }
        if (resolvedExpandReportType === 'pestlReport') {
          return !question.toLowerCase().includes('key takeaways');
        }
        return true;
      })
      .map((question: string) => ({
      name: question,
      value: question,
      label: question,
      tooltip: question,
      ...findIconByName(question),
    }));
  }, [resolvedExpandReportType, reportFollowUpQuestionsMap]);

  return (
    <div className={`user-message-actions ${shouldShowExpandButton ? 'extended' : ''}`}>
      {hasFollowUpQuestions && isLastQuestion && !typing && !streaming ? (
        <Button 
          icon={showFollowUpQuestions ? CloseIcon : QuestionIcon}
          variant={showFollowUpQuestions ? "danger" : "primary"}
          size="small"
          className="user-message-actions__button extended"
          isLoading={isFuqLoading}
          onClick={onToggleFollowUpQuestions}
          aria-label="Deep Dive"
          data-tooltip={showFollowUpQuestions ? 'Hide Deep Dive' : 'Go deeper into your analysis'}
          data-testid="follow-up-questions-button"
        >
          <span className="user-message-actions__button-text">{showFollowUpQuestions ? 'Hide Deep Dive' : 'Deep Dive'}</span>
        </Button>
      ) : null}
      
      {isLastQuestion && !hasImages && !typing && !streaming ? (
        <Button 
          icon={() => <i className="material-icons">bar_chart</i>}
          variant="success"
          size="small"
          className="user-message-actions__button extended"
          isLoading={imagesLoading}
          onClick={() => {
            if (isShareMode) {
              window.location.href = '/sign-up-senior';
            } else {
              onExploreChartClick?.();
              setTimeout(() => scrollToBottom?.(), 600);
            }
          }}
          data-tooltip="Explore the most relevant charts"
          data-testid="explore-charts-button"
        >
          <span className="user-message-actions__button-text">Explore Charts</span>
        </Button>
      ) : null}
      
      <div className="share-dropdown-wrapper" ref={shareDropdownRef}>
        <DropDown 
          itemList={shareDropdownItems}
          defaultItem={{ icon: <ShareIcon />, name: 'Share', value: 'share' }}
          aria-label="Share"
          data-tooltip="Share this report with your team"
          data-testid="share-dropdown"
        />
        {showShareCopied && (
          <div className="share-copied-message">
            <div className="share-copied-message__icon">
              <i className="material-icons">content_copy</i>
            </div>
            <span className="share-copied-message__text">Share link is copied to clipboard</span>
          </div>
        )}
      </div>
      
      {/* Presentation Dropdown */}
      {shouldShowPresentationDropdown && (
        <DropDown
          itemList={presentationDropdownItems}
          onChange={(item) => {
            if (isShareMode) {
              window.location.href = '/sign-up-senior';
            } else {
              contextReportFollowUpClick?.(item.value, index);
              setTimeout(() => scrollToBottom?.(), 600);
            }
          }}
          defaultItem={{ icon: <ColorfulPresentationIcon />, name: 'Presentation', value: '' }}
          aria-label="Presentation"
          data-tooltip="Create an executive-ready deck"
          data-testid="presentation-dropdown"
        />
      )}
      <Button 
        icon={showSources ? CloseIcon : LinkIcon}
        variant={showSources ? "danger" : "primary"}
        size="small"
        shape={shouldShowExpandButton ? "circle" : "rounded"}
        className="user-message-actions__button"
        onClick={onToggleSources}
        disabled={streaming || sourcesLength === 0}
        aria-label={showSources ? 'Hide Sources' : 'Sources'}
        data-tooltip={showSources ? 'Hide Sources' : 'View verified sources'}
        data-testid="sources-button"
      >
        { !shouldShowExpandButton && <span className="user-message-actions__button-text">
          {showSources ? 'Hide Sources' : 'Sources'}
        </span> }
      </Button>
      
      {shouldShowExpandButton &&
        <DropDown 
          itemList={expandButtonItems}
          onChange={(item) => isShareMode ? window.location.href = '/sign-up-senior' : onReportFollowUpClick?.(item.value)}
          defaultItem={{ icon: <ExpandIcon />, name: 'Expand Report', value: '' }}
          aria-label="Expand Report"
          data-tooltip="Explore key highlights"
          data-testid="expand-results-button"
          className="expand-report-dropdown"
        />
      }
    </div>
  );
};

export default UserMessageActions;

